import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as projectService from '../services/projectService';
import * as projectTypeService from '../services/projectTypeService';
import type { ProjectWithDetails, CreateProjectInput } from '../services/projectService';
import type { ProjectType } from '../services/projectTypeService';

export function Projects(): React.ReactElement {
  const { user: currentUser, token } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectWithDetails[]>([]);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [modalError, setModalError] = useState('');
  const [projectTypeSearch, setProjectTypeSearch] = useState('');
  const [createFormData, setCreateFormData] = useState<CreateProjectInput>({
    name: '',
    project_code: '',
    description: '',
    status: 'draft',
    project_type_id: ''
  });

  const canCreateProjects = currentUser?.role === 'company_admin' || currentUser?.role === 'platform_admin' || currentUser?.role === 'project_manager' || currentUser?.role === 'project_manager_lite';

  const loadProjects = async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      const data = await projectService.listProjects(params, token);
      setProjects(data.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter, searchTerm]);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const loadProjectTypes = async () => {
    if (!token) return;
    try {
      const types = await projectTypeService.listProjectTypes({ is_active: true }, token);
      setProjectTypes(types);
    } catch (err) {
      console.error('Failed to load project types:', err);
    }
  };

  const handleCreateClick = async () => {
    if (!token) return;
    setModalError('');
    setProjectTypeSearch('');

    // Generate project code with placeholder name
    try {
      const generatedCode = await projectService.generateProjectCode(token, 'New Project');
      setCreateFormData({
        name: '',
        project_code: generatedCode,
        description: '',
        status: 'draft',
        project_type_id: ''
      });
      await loadProjectTypes();
      setIsCreating(true);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to generate project code');
    }
  };

  const handleCreateProject = async () => {
    if (!token) return;
    if (!createFormData.name) {
      setModalError('Please fill in all required fields');
      return;
    }
    setModalError('');
    try {
      await projectService.createProject(createFormData, token);
      setIsCreating(false);
      setSuccess('Project created successfully');
      loadProjects();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to create project');
    }
  };

  const handleProjectClick = (projectId: string) => {
    navigate('/projects/' + projectId);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400',
      active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
      on_hold: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
      completed: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400',
      archived: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400';
  };

  const getStatusDisplay = (status: string) => {
    const names: Record<string, string> = {
      draft: 'Draft',
      active: 'Active',
      on_hold: 'On Hold',
      completed: 'Completed',
      archived: 'Archived'
    };
    return names[status] || status;
  };

  return (
    <Layout>
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-800 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Page Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
          <h1 className="text-3xl font-bold text-white">Projects</h1>
          <p className="mt-2 text-blue-100">Manage and track all your projects</p>
        </div>

        <div className="bg-surface-primary rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-text-primary">Filter Projects</h2>
            {canCreateProjects && (
              <button onClick={() => navigate('/projects/new')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm">
                <svg className="mr-2 -ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Create Project
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-text-secondary mb-1">Search</label>
              <input type="text" id="search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Project name or code..." className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary" />
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-text-secondary mb-1">Status</label>
              <select id="status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary">
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
          <div className="mt-4 text-sm text-text-secondary">Total Projects: {projects.length}</div>
        </div>

        {isLoading ? (
          <div className="bg-surface-primary rounded-lg shadow p-8 text-center text-text-tertiary">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="bg-surface-primary rounded-lg shadow p-8 text-center">
            <p className="text-text-tertiary mb-4">No projects found</p>
            {canCreateProjects && (
              <button onClick={handleCreateClick} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700">
                Create Your First Project
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div key={project.id} onClick={() => handleProjectClick(project.id)} className="bg-surface-primary rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer p-6">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-text-primary mb-1">{project.name}</h3>
                    <p className="text-sm text-text-tertiary">{project.project_code}</p>
                  </div>
                  <span className={'px-2.5 py-0.5 rounded-full text-xs font-medium ' + getStatusColor(project.status)}>
                    {getStatusDisplay(project.status)}
                  </span>
                </div>
                {project.description && (
                  <p className="text-sm text-text-secondary mb-4 line-clamp-2">{project.description}</p>
                )}
                <div className="border-t border-border-primary pt-4 mt-4">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-text-tertiary">Owner</p>
                      <p className="font-medium text-text-primary">{project.owner_name}</p>
                    </div>
                    <div>
                      <p className="text-text-tertiary">Members</p>
                      <p className="font-medium text-text-primary">{project.member_count}</p>
                    </div>
                    <div>
                      <p className="text-text-tertiary mb-2">Requirements</p>
                      <div className="flex items-center gap-2">
                        <div className="relative group">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 cursor-help">
                            <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">{project.requirement_count}</span>
                          </div>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                            Total Requirements
                          </div>
                        </div>
                        <div className="relative group">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-800 cursor-help">
                            <span className="text-sm font-semibold text-green-700 dark:text-green-400">{project.approved_count}</span>
                          </div>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                            Approved Requirements
                          </div>
                        </div>
                        <div className="relative group">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 cursor-help">
                            <span className="text-sm font-semibold text-red-700 dark:text-red-400">{project.rejected_count}</span>
                          </div>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                            Rejected Requirements
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {project.target_completion_date && (
                    <div className="mt-3 text-sm">
                      <p className="text-text-tertiary">Target Date</p>
                      <p className="font-medium text-text-primary">{formatDate(project.target_completion_date)}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isCreating && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={() => { setIsCreating(false); setModalError(''); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-text-primary mb-4">Create New Project</h3>
                {modalError && (
                  <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-800 dark:text-red-400">{modalError}</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Project Name <span className="text-red-500 dark:text-red-400">*</span></label>
                    <input type="text" value={createFormData.name} onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })} className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary" placeholder="My Project" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Project Code</label>
                    <input
                      type="text"
                      value={createFormData.project_code}
                      readOnly
                      className="w-full px-3 py-2 border border-border-primary rounded-md bg-background-secondary text-text-tertiary cursor-not-allowed"
                      placeholder="Auto-generated"
                    />
                    <p className="mt-1 text-xs text-text-tertiary">Automatically generated based on your company name</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
                    <textarea value={createFormData.description} onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary" placeholder="Project description..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Project Type</label>
                    <input
                      type="text"
                      value={projectTypeSearch}
                      onChange={(e) => setProjectTypeSearch(e.target.value)}
                      placeholder="Search project types..."
                      className="w-full px-3 py-2 mb-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary"
                    />
                    <select
                      value={createFormData.project_type_id || ''}
                      onChange={(e) => setCreateFormData({ ...createFormData, project_type_id: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary"
                    >
                      <option value="">-- Select a project type --</option>
                      {projectTypes
                        .filter(type =>
                          projectTypeSearch === '' ||
                          type.name.toLowerCase().includes(projectTypeSearch.toLowerCase()) ||
                          type.category.toLowerCase().includes(projectTypeSearch.toLowerCase())
                        )
                        .map((type) => (
                          <option key={type.id} value={type.id}>{type.name} ({type.category})</option>
                        ))}
                    </select>
                    <p className="mt-1 text-xs text-text-tertiary">Optional: Search and select a project type{projectTypeSearch && ` (${projectTypes.filter(type =>
                      projectTypeSearch === '' ||
                      type.name.toLowerCase().includes(projectTypeSearch.toLowerCase()) ||
                      type.category.toLowerCase().includes(projectTypeSearch.toLowerCase())
                    ).length} results)`}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Status</label>
                    <select value={createFormData.status} onChange={(e) => setCreateFormData({ ...createFormData, status: e.target.value as any })} className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary">
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Start Date</label>
                    <input type="date" value={createFormData.start_date} onChange={(e) => setCreateFormData({ ...createFormData, start_date: e.target.value })} className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Target Completion Date</label>
                    <input type="date" value={createFormData.target_completion_date} onChange={(e) => setCreateFormData({ ...createFormData, target_completion_date: e.target.value })} className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary text-text-primary" />
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button onClick={handleCreateProject} className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-base font-medium text-white hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm">
                  Create Project
                </button>
                <button onClick={() => { setIsCreating(false); setModalError(''); }} className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Projects;
